import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageSquareText } from "lucide-react";
import ChatPanel from "./ChatPanel";
import { CHAT_GREETING } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORE = "chatproz-chat";
const TIMEOUT_MS = 60000;
const MAX_LEN = 2000;

const uid = () => Math.random().toString(36).slice(2, 10);

const loadSaved = () => {
    try {
        const parsed = JSON.parse(sessionStorage.getItem(STORE) || "null");
        if (parsed && parsed.sessionId && Array.isArray(parsed.messages)) return parsed;
    } catch (e) {
        /* ignore corrupt storage */
    }
    return null;
};

const ChatWidget = () => {
    const saved = useRef(loadSaved());
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [sessionId] = useState(
        () =>
            saved.current?.sessionId ||
            `web-${typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : uid() + Date.now()}`
    );
    const [messages, setMessages] = useState(
        () => saved.current?.messages || [{ id: "greeting", role: "assistant", text: CHAT_GREETING }]
    );

    useEffect(() => {
        try {
            sessionStorage.setItem(STORE, JSON.stringify({ sessionId, messages }));
        } catch (e) {
            /* storage full or blocked */
        }
    }, [sessionId, messages]);

    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && setOpen(false);
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    const send = useCallback(
        async (raw, resend = false) => {
            const text = (raw || "").trim().slice(0, MAX_LEN);
            if (!text || busy) return;
            setMessages((m) => {
                const clean = m.filter((x) => !x.error);
                return resend ? clean : [...clean, { id: uid(), role: "user", text }];
            });
            setBusy(true);
            const ctrl = new AbortController();
            const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
            try {
                const res = await fetch(`${API}/chat`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ message: text, session_id: sessionId, page: window.location.pathname }),
                    signal: ctrl.signal,
                });
                const data = await res.json().catch(() => ({}));
                if (!res.ok) throw new Error(res.status === 503 ? "offline" : res.status === 429 ? "flood" : "failed");
                setMessages((m) => [...m, { id: uid(), role: "assistant", text: data.text }]);
            } catch (err) {
                const reason =
                    err.message === "offline"
                        ? "Our assistant is being connected right now. Please reach us on WhatsApp and we will reply straight away."
                        : err.message === "flood"
                          ? "That is a lot of messages in a short while. Please wait a minute and try again."
                          : err.name === "AbortError"
                            ? "That took longer than expected. Please send that again."
                            : "Sorry, something went wrong on our side. Please try again in a moment.";
                setMessages((m) => [...m, { id: uid(), role: "assistant", text: reason, error: true, retry: text }]);
            } finally {
                clearTimeout(timer);
                setBusy(false);
            }
        },
        [busy, sessionId]
    );

    return (
        <>
            <AnimatePresence>
                {!open && (
                    <motion.button
                        type="button"
                        onClick={() => setOpen(true)}
                        data-testid="chat-launcher-button"
                        aria-label="Open the ChatProz assistant"
                        initial={{ opacity: 0, scale: 0.6, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.6 }}
                        transition={{ delay: 1.4, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.92 }}
                        className="fixed right-5 bottom-[88px] md:right-6 md:bottom-6 z-[55] flex h-14 w-14 items-center justify-center rounded-full bg-navy text-white shadow-[0_18px_40px_-12px_rgba(28,28,28,0.65)] ring-1 ring-accent/40"
                    >
                        <span
                            className="pointer-events-none absolute inset-0 rounded-full ring-2 ring-accent/70 animate-ping [animation-iteration-count:3]"
                            aria-hidden="true"
                        />
                        <MessageSquareText className="h-6 w-6 text-accent" />
                    </motion.button>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {open && (
                    <motion.div
                        key="chat-panel"
                        initial={{ opacity: 0, y: 24, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 16, scale: 0.98 }}
                        transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                        className="fixed inset-x-3 bottom-3 top-[76px] md:inset-auto md:right-6 md:bottom-6 md:top-auto md:h-[620px] md:max-h-[calc(100vh-7rem)] md:w-[400px] z-[60]"
                        data-testid="chat-panel"
                    >
                        <ChatPanel
                            messages={messages}
                            busy={busy}
                            showStarters={messages.length === 1 && !busy}
                            onSend={send}
                            onRetry={(text) => send(text, true)}
                            onClose={() => setOpen(false)}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default ChatWidget;
